import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { MAIN_SITE_URL } from '@/mocks/categories';

const SITE_URL = import.meta.env.VITE_SITE_URL;

const SECTION_IDS = ['s1', 's2', 's3', 's4', 's5', 's6', 's7'];

export default function Guide() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const sections = SECTION_IDS.map((id) => ({
    id,
    title: t(`article.${id}Title`),
    body: t(`article.${id}Body`),
  }));

  const headers = t('article.tableHeaders', { returnObjects: true }) as unknown as string[];
  const rows = t('article.tableRows', { returnObjects: true }) as unknown as string[][];

  const faqItems = [
    { q: t('article.faq1q'), a: t('article.faq1a') },
    { q: t('article.faq2q'), a: t('article.faq2a') },
    { q: t('article.faq3q'), a: t('article.faq3a') },
    { q: t('article.faq4q'), a: t('article.faq4a') },
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: t('article.title'),
        description: t('meta.articleDesc'),
        datePublished: '2026-01-15',
        dateModified: '2026-08-20',
        author: { '@type': 'Organization', name: 'USFansSheet Editorial Team' },
        publisher: { '@type': 'Organization', name: 'USFansSheet.help' },
        mainEntityOfPage: `${SITE_URL}/usfans-spreadsheet-guide`,
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-24 md:pt-28">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

        <article className="mx-auto max-w-3xl px-4 md:px-6 py-8 md:py-12">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm">
            <Link to="/" className="inline-flex items-center gap-1.5 text-primary-700 font-medium hover:underline whitespace-nowrap">
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-arrow-left-line text-sm" />
              </span>
              {t('article.backHome')}
            </Link>
          </nav>

          {/* Header */}
          <header className="mb-8">
            <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-4xl leading-tight mb-4">
              {t('article.title')}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-calendar-line text-base" />
                {t('article.updated')}: 2026
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-base" />
                {t('article.readTime')}: {t('guideCard.readTime')}
              </span>
            </div>
          </header>

          {/* Quick Answer */}
          <div className="mb-8 bg-accent-100/70 border border-accent-300/50 rounded-lg p-5 md:p-6">
            <h2 className="font-heading font-semibold text-foreground-900 text-base md:text-lg mb-2">
              {t('article.quickAnswerTitle')}
            </h2>
            <p className="text-foreground-700 text-sm md:text-base leading-relaxed">{t('article.quickAnswer')}</p>
          </div>

          {/* Table of contents */}
          <nav className="mb-10 bg-background-100 border border-background-200 rounded-lg p-5">
            <h2 className="font-heading font-semibold text-foreground-900 text-base mb-3">{t('article.tocTitle')}</h2>
            <ul className="space-y-2">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-sm text-primary-700 hover:underline whitespace-nowrap">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Sections */}
          <div className="space-y-10">
            {sections.map((s) => (
              <section key={s.id} id={s.id}>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-3">
                  {s.title}
                </h2>
                <p className="text-foreground-700 text-sm md:text-base leading-relaxed">{s.body}</p>
              </section>
            ))}
          </div>

          {/* Comparison table */}
          <section className="mt-12">
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
              {t('article.tableTitle')}
            </h2>
            <div className="overflow-x-auto border border-background-200 rounded-lg">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {headers.map((h) => (
                      <th key={h} className="text-left px-4 py-3 font-heading font-semibold text-foreground-900 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t border-background-200">
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={`px-4 py-3 ${
                            j === 0 ? 'font-semibold text-foreground-900' : 'text-foreground-600'
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Article FAQ */}
          <section className="mt-12">
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-6">
              {t('article.faqTitle')}
            </h2>
            <div className="space-y-3">
              {faqItems.map((item, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <div key={item.q} className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="font-heading font-semibold text-foreground-900 text-sm md:text-base">
                        {item.q}
                      </span>
                      <span className={`flex items-center justify-center w-6 h-6 text-foreground-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-lg" />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5 text-foreground-600 text-sm md:text-base leading-relaxed">{item.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Related links */}
          <section className="mt-12 bg-background-100 border border-background-200 rounded-lg p-6">
            <h2 className="font-heading font-semibold text-foreground-900 text-lg mb-4">{t('article.relatedTitle')}</h2>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="inline-flex items-center gap-2 text-primary-700 text-sm hover:underline whitespace-nowrap">
                  <i className="ri-home-5-line text-base" />
                  {t('article.related1')}
                </Link>
              </li>
              <li>
                <Link to="/#categories" className="inline-flex items-center gap-2 text-primary-700 text-sm hover:underline whitespace-nowrap">
                  <i className="ri-grid-line text-base" />
                  {t('article.related2')}
                </Link>
              </li>
            </ul>
          </section>

          {/* CTA */}
          <section className="mt-12 text-center bg-primary-500 rounded-lg p-8 md:p-10">
            <h2 className="font-heading font-bold text-background-50 text-xl md:text-2xl mb-2">
              {t('article.ctaTitle')}
            </h2>
            <p className="text-background-50/90 text-sm md:text-base mb-6">{t('article.ctaBody')}</p>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-lg bg-background-50 text-foreground-950 font-semibold text-sm md:text-base hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              {t('article.cta')}
              <span className="flex items-center justify-center w-5 h-5">
                <i className="ri-external-link-line text-base" />
              </span>
            </a>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}